/**
 * Header Component
 */

import { LogOut, User, PanelLeftClose, PanelLeftOpen, Moon, Sun } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/hooks';
import { useTheme } from '@/providers/ThemeProvider';
import { Button } from '@/components/ui';
import { AlertBell } from '@/features/alerts/components/AlertBell';
import { cn } from '@/lib/utils';

import { MobileNav } from './nav';

export function Header({ sidebarCollapsed, onToggleSidebar }: { sidebarCollapsed: boolean; onToggleSidebar: () => void }) {
  const { user, logout } = useAuth();
  const { setTheme, resolvedTheme } = useTheme();
  const { t } = useTranslation('nav');
  const toggleLabel = sidebarCollapsed ? t('aria.expandSidebar') : t('aria.collapseSidebar');

  return (
    <header className="relative flex h-16 items-center justify-between border-b bg-card/80 px-4 backdrop-blur supports-[backdrop-filter]:bg-card/70 sm:px-6">
      <div className="flex items-center gap-3">
        <MobileNav />
        <Button variant="ghost" size="icon" onClick={onToggleSidebar} aria-label={toggleLabel} title={toggleLabel} className="hidden md:inline-flex">
          {sidebarCollapsed ? <PanelLeftOpen className="h-4 w-4" aria-hidden /> : <PanelLeftClose className="h-4 w-4" aria-hidden />}
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <AlertBell />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
          aria-label={resolvedTheme === 'dark' ? t('aria.lightMode') : t('aria.darkMode')}
          title={resolvedTheme === 'dark' ? t('aria.lightMode') : t('aria.darkMode')}
        >
          {resolvedTheme === 'dark' ? <Sun className="h-4 w-4" aria-hidden /> : <Moon className="h-4 w-4" aria-hidden />}
        </Button>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button variant="ghost" className="flex items-center gap-2 pl-1.5" aria-label={t('aria.userMenu')}>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground ring-2 ring-primary/20">
                <User className="h-4 w-4" aria-hidden />
              </div>
              <span className="hidden text-sm font-medium sm:inline">
                {user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email?.split('@')[0]}
              </span>
            </Button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className={cn(
                'z-50 min-w-[220px] rounded-xl border bg-popover p-1.5 shadow-lg',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
              )}
              sideOffset={8}
              align="end"
            >
              <div className="mb-1 border-b px-2.5 py-2">
                <p className="truncate text-sm font-medium text-foreground">{user?.email}</p>
                <p className="text-xs capitalize text-muted-foreground">{user?.role}</p>
              </div>

              <DropdownMenu.Item
                className="relative flex cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-2 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                onClick={logout}
              >
                <LogOut className="h-4 w-4" aria-hidden />
                {t('userMenu.signOut')}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}
